import { v4 as uuidv4 } from "uuid";
import { notify } from "../api/photoshop";
import { photoshop } from "../globals";
import { useListenFromRenderer } from "../message";
import { postToRenderer } from "../message/utils";
import { base64ToUint8Array } from "../utils/jimp";
import { isCropToolActive } from "./shared/check-crop-tool";
import { fetchInferenceJob } from "./shared/fetch-inference-job";

const TOOL_NAME = "Icon Generator";
const PRESET_DOCUMENT_ID = "qe6jgkno6dnow7elodyswt7i";
const CUSTOM_DOCUMENT_ID = "sgq2j8kl4j6hj9wcezfyyapd";

const callGenerateIcon = async (
	messageId: string,
	prompt: string,
	jwt: string,
	tool_document_id: string,
	batch_size: number,
	resolution: string,
	background_remover: boolean,
	prompt_index?: number,
	prompt_document_id?: string,
	reference_image?: string,
) => {
	let success = false;

	if (isCropToolActive()) {
		await notify("Cannot generate images while the Crop tool is active.");
		return success;
	}

	await photoshop.core.executeAsModal(
		async (executionContext) => {
			const doc = photoshop.app.activeDocument;
			if (!doc) {
				await notify("Open a PSD file first");
				return;
			}

			await doc.suspendHistory(async () => {
				try {
					const refImgBase64 = reference_image?.startsWith("data:image/")
						? reference_image.split(",")[1]
						: reference_image;
					const isPreset = prompt_index !== undefined;
					const data = {
						prompt_document_id: isPreset ? (prompt_document_id ?? PRESET_DOCUMENT_ID) : CUSTOM_DOCUMENT_ID,
						prompt_index: prompt_index ?? 0,
						prompt_variables: { icon_text: prompt },
						input_images: refImgBase64 ? [refImgBase64] : [],
						from_editor: false,
						aspect_ratio: "1:1",
						jwt,
						tool_document_id,
						batch_size,
						resolution,
						background_remover,
					};

					const result = await fetchGenerateIcon(data, executionContext, messageId);
					if (!result) return;

					// Safely get selection bounds
					let selLeft = 0, selTop = 0, selWidth = doc.width, selHeight = doc.height;
					try {
						const [selResult] = await photoshop.app.batchPlay(
							[{ _obj: "get", _target: [{ _property: "selection" }, { _ref: "document", _id: doc.id }] }],
							{ synchronousExecution: false },
						);
						const sel = selResult?.selection;
						if (sel && sel._enum !== "none" && sel.left !== undefined) {
							const v = (x: any): number => (typeof x === "object" && x !== null ? x._value : x);
							selLeft = v(sel.left);
							selTop = v(sel.top);
							selWidth = v(sel.right) - selLeft;
							selHeight = v(sel.bottom) - selTop;
						}
					} catch (_e) {
						// No selection or unsupported — fall back to canvas
					}
					const targetSize = Math.min(selWidth, selHeight);

					const resultImages = Array.isArray(result) ? result : [result];

					for (let i = 0; i < resultImages.length; i++) {
						const { width, height, rawPixels } = base64ToUint8Array(resultImages[i]);
						const pixels = new Uint8Array(rawPixels);

						// 배경 제거 시 Smart Object 트리밍 방지: 네 귀퉁이에 alpha=1 설정
						if (background_remover && width > 0 && height > 0) {
							const corners = [
								0,
								(width - 1) * 4,
								(height - 1) * width * 4,
								((height - 1) * width + (width - 1)) * 4,
							];
							for (const offset of corners) {
								if (pixels[offset + 3] === 0) {
									pixels[offset + 3] = 1;
								}
							}
						}

						const resultImageData = await photoshop.imaging.createImageDataFromBuffer(pixels, {
							width,
							height,
							components: 4,
							colorSpace: "RGB",
						});

						const layerName = resultImages.length > 1
							? `${TOOL_NAME} ${i + 1}`
							: TOOL_NAME;

						const newLayer = await doc.createPixelLayer({ name: layerName });
						if (!newLayer) {
							resultImageData.dispose();
							continue;
						}

						await photoshop.imaging.putPixels({
							documentID: doc.id,
							layerID: newLayer.id,
							imageData: resultImageData,
							targetBounds: { left: 0, top: 0, right: width, bottom: height },
							replace: true,
						});

						newLayer.selected = true;
						await photoshop.app.batchPlay(
							[{ _obj: "newPlacedLayer" }],
							{ synchronousExecution: true, modalBehavior: "execute" },
						);

						// Deselect before transforming to avoid unit-type conflicts
						await photoshop.app.batchPlay(
							[{ _obj: "set", _target: [{ _ref: "channel", _property: "selection" }], to: { _enum: "ordinal", _value: "none" } }],
							{ synchronousExecution: true, modalBehavior: "execute" },
						);

						const smartLayer = doc.activeLayers[0];
						const scaleFactor = Math.min(targetSize / width, targetSize / height, 1);
						if (scaleFactor < 1) {
							await smartLayer.scale(
								scaleFactor * 100,
								scaleFactor * 100,
								photoshop.constants.AnchorPosition.TOPLEFT,
							);
						}

						const scaledSize = Math.round(Math.min(width, height) * scaleFactor);
						const moveX = Math.round(selLeft + (selWidth - scaledSize) / 2);
						const moveY = Math.round(selTop + (selHeight - scaledSize) / 2);
						if (moveX !== 0 || moveY !== 0) {
							await photoshop.app.batchPlay(
								[{
									_obj: "move",
									_target: [{ _ref: "layer", _enum: "ordinal", _value: "targetEnum" }],
									to: {
										_obj: "offset",
										horizontal: { _unit: "pixelsUnit", _value: moveX },
										vertical: { _unit: "pixelsUnit", _value: moveY },
									},
								}],
								{ synchronousExecution: true, modalBehavior: "execute" },
							);
						}

						await resultImageData.dispose();
					}

					success = true;
				} catch (err) {
					console.error(err);
					await notify(`${err}`);
				}
			}, TOOL_NAME);
		},
		{ commandName: TOOL_NAME },
	);

	return success;
};

export async function fetchGenerateIcon(
	params: {
		prompt_document_id: string;
		prompt_index: number;
		prompt_variables: { icon_text: string };
		input_images: string[];
		from_editor: boolean;
		resolution: string;
		background_remover: boolean;
	},
	executionContext: any,
	messageId: string,
): Promise<string | string[] | false> {
	return fetchInferenceJob({
		params,
		executionContext,
		messageId,
		apiPath: "/infer/oplux",
		statusEndpoint: (apiUrl, jobId) =>
			`${apiUrl.replace("/infer/oplux", "")}/status/${jobId}`,
		resultEndpoint: (apiUrl, jobId) =>
			`${apiUrl.replace("/infer/oplux", "")}/result/${jobId}`,
		cancelEndpoint: (apiUrl, jobId) => ({
			url: `${apiUrl.replace("/infer/oplux", "")}/cancel/${jobId}`,
			method: "POST",
		}),
		extractResult: (data) => data.image_base64_list ?? [data.image_base64],
		useGatewayUrl: true,
	});
}

export function useListenGenerateIconMessage() {
	useListenFromRenderer([
		{
			key: "generateIcon",
			onMessage: async (message) => {
				const success = await callGenerateIcon(
					message.id,
					message.values.prompt,
					message.values.jwt,
					message.values.tool_document_id,
					message.values.batch_size,
					message.values.resolution,
					message.values.background_remover,
					message.values.prompt_index,
					message.values.prompt_document_id,
					message.values.reference_image,
				);
				postToRenderer([
					{
						key: "finish",
						id: uuidv4(),
						values: {
							id: message.id,
							status: success ? "success" : "error",
						},
					},
				]);
			},
		},
	]);
}
